import { Box, Typography, Paper, Grid } from '@mui/material'
import { styled, keyframes } from '@mui/material/styles'
import { useState, useEffect, useRef } from 'react'
import { COLORS } from '../../constants/colors'
import { TEXT } from '../../constants/text'

const pulse = keyframes`
  0%   { opacity: 0.8; transform: scale(1); }
  50%  { opacity: 1;   transform: scale(1.02); }
  100% { opacity: 0.8; transform: scale(1); }
`

const TickerBox = styled(Paper)(() => ({
  background: 'linear-gradient(135deg, rgba(0,0,0,0.9) 0%, rgba(17,17,17,0.95) 100%)',
  border: `1px solid ${COLORS.gold.border}`,
  borderRadius: 15,
  padding: '16px 20px',
  position: 'relative',
  overflow: 'hidden',
}))

const StatCard = styled(Box)(({ variant = 'default' }) => {
  const palette = {
    profit:  { bg: COLORS.green.muted, border: COLORS.green.border },
    danger:  { bg: COLORS.red.muted,   border: COLORS.red.border },
    premium: { bg: COLORS.gold.muted,  border: COLORS.gold.border },
    default: { bg: COLORS.blue.muted,  border: COLORS.blue.border },
  }
  const p = palette[variant] || palette.default
  return {
    background: p.bg,
    border: `1px solid ${p.border}`,
    borderRadius: 10,
    padding: '10px 14px',
    textAlign: 'center',
    animation: `${pulse} 2s ease-in-out infinite`,
    transition: 'all .3s',
    '&:hover': { transform: 'scale(1.04)' },
  }
})

const ScrollBar = styled(Box)(() => ({
  width: '100%',
  overflow: 'hidden',
  background: 'rgba(0,0,0,0.5)',
  borderRadius: 5,
  height: 24,
  marginTop: 12,
  position: 'relative',
}))

const ScrollTrack = styled(Box)(() => ({
  display: 'flex',
  whiteSpace: 'nowrap',
  gap: 40,
  willChange: 'transform',
}))

/**
 * Live market ticker — summary bar with scrolling tokens.
 */
export default function LiveTicker({ stats = {} }) {
  const { totalSignals = 0, rugsPrevented = 0, whaleAlerts = 0, avgRugScore = 0, tokens = [] } = stats
  const scrollRef = useRef(null)
  const posRef = useRef(0)

  // Smooth scroll animation
  useEffect(() => {
    let raf
    const el = scrollRef.current
    if (!el || tokens.length === 0) return

    const step = () => {
      posRef.current -= 0.5
      if (Math.abs(posRef.current) >= el.scrollWidth / 2) posRef.current = 0
      el.style.transform = `translateX(${posRef.current}px)`
      raf = requestAnimationFrame(step)
    }
    raf = requestAnimationFrame(step)
    return () => cancelAnimationFrame(raf)
  }, [tokens])

  return (
    <TickerBox elevation={0}>
      {/* Top stats row */}
      <Grid container spacing={1.5} sx={{ mb: 1 }}>
        <Grid item xs={6} sm={3}>
          <StatCard variant="default">
            <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: COLORS.text.muted }}>
              Signals
            </Typography>
            <Typography sx={{ fontWeight: 900, fontSize: '1.1rem', color: COLORS.blue.primary }} className="mono">
              {totalSignals.toLocaleString()}
            </Typography>
          </StatCard>
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard variant="danger">
            <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: COLORS.text.muted }}>
              Rugs Caught
            </Typography>
            <Typography sx={{ fontWeight: 900, fontSize: '1.1rem', color: COLORS.red.primary }} className="mono">
              {rugsPrevented.toLocaleString()}
            </Typography>
          </StatCard>
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard variant="premium">
            <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: COLORS.text.muted }}>
              Whale Alerts
            </Typography>
            <Typography sx={{ fontWeight: 900, fontSize: '1.1rem', color: COLORS.gold.primary }} className="mono">
              {whaleAlerts.toLocaleString()}
            </Typography>
          </StatCard>
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard variant="profit">
            <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: COLORS.text.muted }}>
              Avg Rug Score
            </Typography>
            <Typography sx={{ fontWeight: 900, fontSize: '1.1rem', color: COLORS.green.primary }} className="mono">
              {avgRugScore}
            </Typography>
          </StatCard>
        </Grid>
      </Grid>

      {/* Scrolling token bar */}
      {tokens.length > 0 && (
        <ScrollBar>
          <ScrollTrack ref={scrollRef}>
            {[...tokens, ...tokens].map((t, i) => (
              <Box key={i} sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: COLORS.text.primary }}>
                  {t.symbol}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    fontFamily: "'JetBrains Mono', monospace",
                    color: t.change >= 0 ? COLORS.green.primary : COLORS.red.primary,
                  }}
                >
                  {t.change >= 0 ? '+' : ''}{t.change?.toFixed(1)}%
                </Typography>
              </Box>
            ))}
          </ScrollTrack>
        </ScrollBar>
      )}
    </TickerBox>
  )
}
